import {createPortal} from "react-dom";
import {useEffect, useRef} from "react";
import classNames from "classnames";
import Input from "../input";

export default function BrickInsertPopover({
    open,
    panelRef,
    panelStyle,
    source,
    search,
    searchRef,
    options,
    highlightedOptionId,
    onSearchChange,
    onKeyDown,
    onOptionHover,
    onOptionSelect,
    onCancel
}) {
    const optionsContainerRef = useRef(null);

    useEffect(() => {
        if (!open || !highlightedOptionId) return;

        const container = optionsContainerRef.current;
        if (!container) return;

        const highlightedOption = Array.from(container.querySelectorAll('[data-brick-option-id]'))
            .find((node) => node.getAttribute('data-brick-option-id') === highlightedOptionId);

        if (!highlightedOption) return;

        highlightedOption.scrollIntoView({
            block: 'nearest'
        });
    }, [highlightedOptionId, open]);

    if (!open) return null;

    return createPortal(
        <div
            ref={panelRef}
            style={panelStyle || {position: 'fixed', left: '-9999px', top: '-9999px', width: '352px', zIndex: 1200, visibility: 'hidden'}}
            className="rounded-xl border border-gray-200 bg-white p-3 shadow-xl"
        >
            <div className="mb-2 text-sm font-medium text-gray-900">Insert Dynamic Content</div>
            {source === 'toolbar' && (
                <div className="mb-2">
                    <Input
                        ref={searchRef}
                        name="brick-search"
                        type="text"
                        placeholder="Search dynamic content..."
                        value={search}
                        onChange={(_event, _name, nextValue) => onSearchChange(nextValue)}
                        onKeyDown={onKeyDown}
                    />
                </div>
            )}
            <div
                ref={optionsContainerRef}
                className="overflow-y-auto"
                style={{maxHeight: 'min(18rem, calc(100vh - 10rem))'}}
            >
                {options.map((option, index) => {
                    if (option.type === 'group') {
                        return (
                            <div
                                key={option.key || option.label}
                                className="px-2.5 pt-2 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-00"
                            >
                                {option.label}
                            </div>
                        );
                    }

                    return (
                        <button
                            key={option.id}
                            type="button"
                            className={classNames(
                                "w-full rounded-md px-2.5 py-2 text-left hover:bg-gray-100",
                                option.id === highlightedOptionId ? "bg-gray-100" : null
                            )}
                            data-brick-option-id={option.id}
                            aria-selected={option.id === highlightedOptionId}
                            onMouseEnter={() => onOptionHover(index)}
                            onClick={() => onOptionSelect(option)}
                        >
                            <div className="text-sm font-medium text-gray-900">{option.label}</div>
                            {option.description ? (
                                <div className="text-xs text-gray-500">{option.description}</div>
                            ) : null}
                        </button>
                    );
                })}
                {!options.some((option) => option.type !== 'group') && (
                    <div className="px-2.5 py-2 text-sm text-gray-500">No matches</div>
                )}
            </div>
            <div className="mt-3 flex justify-end gap-3 border-t border-gray-200 pt-3">
                <button
                    type="button"
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                    onClick={onCancel}
                >
                    Cancel
                </button>
            </div>
        </div>,
        document.body
    );
}
